import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet,
  SafeAreaView, StatusBar, Alert, RefreshControl, TextInput, Modal,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

const API = 'https://api.jobeng.ai';
const KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const P = '#0e4e96';

const getH = async () => {
  const t = await AsyncStorage.getItem('auth_token').catch(() => null);
  return { 'Content-Type': 'application/json', 'x-api-key': KEY, ...(t ? { Authorization: `Bearer ${t}` } : {}) };
};

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const STATUS_COLORS = { pending: '#f59e0b', approved: '#10b981', rejected: '#ef4444' };
const LEAVE_TYPES = ['Annual', 'Sick', 'Maternity', 'Paternity', 'Compassionate', 'Study', 'Unpaid', 'Other'];

export default function LeaveRequestScreen({ navigation, route }) {
  const empId = route?.params?.employeeId || route?.params?.employee?.id || '1';
  const [requests, setRequests] = useState([]);
  const [balance, setBalance] = useState({ annual: 25, used: 0, remaining: 25, sick: 10 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ leaveType: 'Annual', startDate: '', endDate: '', reason: '' });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const h = await getH();
      const [reqRes, empRes] = await Promise.allSettled([
        fetch(`${API}/api/leave/requests/${parseInt(empId)||0}`, { headers: h }),
        fetch(`${API}/api/employee/dashboard/${parseInt(empId)||0}`, { headers: h }),
      ]);
      if (reqRes.status === 'fulfilled' && reqRes.value.ok) {
        const json = await reqRes.value.json().catch(() => ({}));
        setRequests(Array.isArray(json?.data) ? json.data : []);
      }
      if (empRes.status === 'fulfilled' && empRes.value.ok) {
        const json = await empRes.value.json().catch(() => ({}));
        const d = json?.data;
        if (d) setBalance({
          annual: parseInt(d.annual_leave_total || 25),
          used: parseInt(d.leave_used || 0),
          remaining: parseInt(d.leave_remaining || 25),
          sick: parseInt(d.sick_leave_total || 10),
        });
      }
    } catch (e) { console.warn('LeaveRequest load:', e.message); }
    finally { setLoading(false); setRefreshing(false); }
  }, [empId]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.startDate || !form.endDate) { Alert.alert('Missing dates', 'Please enter start and end dates'); return; }
    const start = new Date(form.startDate), end = new Date(form.endDate);
    if (isNaN(start) || isNaN(end)) { Alert.alert('Invalid dates', 'Use format YYYY-MM-DD'); return; }
    if (end < start) { Alert.alert('Invalid range', 'End date must be after start date'); return; }
    const days = Math.ceil((end - start) / 86400000) + 1;
    setSubmitting(true);
    try {
      const h = await getH();
      const res = await fetch(`${API}/api/leave/request`, {
        method: 'POST', headers: h,
        body: JSON.stringify({
          employeeId: empId, leaveType: form.leaveType,
          startDate: form.startDate, endDate: form.endDate,
          days, reason: form.reason,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (json.success || res.ok) {
        Alert.alert('✅ Submitted', `${days} day(s) of ${form.leaveType} leave requested`);
        setShowModal(false);
        setForm({ leaveType: 'Annual', startDate: '', endDate: '', reason: '' });
        load();
      } else Alert.alert('Error', json.error || 'Could not submit request');
    } catch (e) { Alert.alert('Error', e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <SafeAreaView style={s.safe}>
      <StatusBar barStyle="light-content" backgroundColor={P} />
      <LinearGradient colors={[P, '#1e7be2']} style={s.header}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={s.back}>
          <MaterialCommunityIcons name="arrow-left" size={22} color="#fff" />
        </TouchableOpacity>
        <Text style={s.headerTitle}>Leave Requests</Text>
        <TouchableOpacity onPress={() => setShowModal(true)} style={s.addBtn}>
          <MaterialCommunityIcons name="plus" size={22} color="#fff" />
        </TouchableOpacity>
      </LinearGradient>

      <ScrollView refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); load(); }} colors={[P]} />}>
        {/* Balance Cards */}
        <View style={s.balanceRow}>
          {[
            { label: 'Annual Leave', total: balance.annual, used: balance.used, remaining: balance.remaining, color: P },
            { label: 'Sick Leave', total: balance.sick, used: 0, remaining: balance.sick, color: '#10b981' },
          ].map(({ label, total, used, remaining, color }) => (
            <View key={label} style={[s.balanceCard, { borderTopColor: color }]}>
              <Text style={s.balanceLabel}>{label}</Text>
              <Text style={[s.balanceDays, { color }]}>{remaining}</Text>
              <Text style={s.balanceSub}>of {total} days remaining</Text>
              <View style={s.progressBar}>
                <View style={[s.progressFill, { width: `${Math.max(0, (remaining / total) * 100)}%`, backgroundColor: color }]} />
              </View>
            </View>
          ))}
        </View>

        {/* Requests */}
        <View style={s.section}>
          <Text style={s.sectionTitle}>My Requests</Text>
          {requests.length === 0 && !loading && (
            <View style={s.emptyContainer}>
              <MaterialCommunityIcons name="calendar-blank" size={48} color="#cbd5e1" />
              <Text style={s.empty}>No leave requests yet</Text>
              <TouchableOpacity style={s.emptyBtn} onPress={() => setShowModal(true)}>
                <Text style={s.emptyBtnText}>Request Leave</Text>
              </TouchableOpacity>
            </View>
          )}
          {requests.map((r, i) => (
            <View key={r.id || i} style={s.requestCard}>
              <View style={[s.typeBadge, { backgroundColor: (STATUS_COLORS[r.status] || '#64748b') + '20' }]}>
                <Text style={[s.typeText, { color: STATUS_COLORS[r.status] || '#64748b' }]}>{r.leave_type || r.leaveType}</Text>
              </View>
              <View style={{ flex: 1, marginLeft: 10 }}>
                <Text style={s.requestDates}>{fmt(r.start_date || r.startDate)} → {fmt(r.end_date || r.endDate)}</Text>
                <Text style={s.requestReason} numberOfLines={1}>{r.reason || 'No reason provided'}</Text>
              </View>
              <View style={[s.statusBadge, { backgroundColor: (STATUS_COLORS[r.status] || '#64748b') + '15' }]}>
                <Text style={[s.statusText, { color: STATUS_COLORS[r.status] || '#64748b' }]}>{(r.status || 'pending').toUpperCase()}</Text>
              </View>
            </View>
          ))}
        </View>
      </ScrollView>

      {/* Submit Modal */}
      <Modal visible={showModal} animationType="slide" presentationStyle="pageSheet">
        <SafeAreaView style={{ flex: 1, backgroundColor: '#f8fafc' }}>
          <View style={s.modalHeader}>
            <TouchableOpacity onPress={() => setShowModal(false)}><MaterialCommunityIcons name="close" size={22} color="#1e293b" /></TouchableOpacity>
            <Text style={s.modalTitle}>New Leave Request</Text>
            <TouchableOpacity onPress={submit} disabled={submitting}>
              <Text style={[s.submitText, submitting && { opacity: 0.5 }]}>{submitting ? 'Submitting...' : 'Submit'}</Text>
            </TouchableOpacity>
          </View>
          <ScrollView style={{ padding: 16 }}>
            <Text style={s.fieldLabel}>Leave Type</Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ marginBottom: 16 }}>
              {LEAVE_TYPES.map(t => (
                <TouchableOpacity key={t} onPress={() => setForm(p => ({ ...p, leaveType: t }))}
                  style={[s.chip, form.leaveType === t && s.chipActive]}>
                  <Text style={[s.chipText, form.leaveType === t && s.chipTextActive]}>{t}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
            <Text style={s.fieldLabel}>Start Date (YYYY-MM-DD)</Text>
            <TextInput style={s.input} value={form.startDate} onChangeText={v => setForm(p => ({ ...p, startDate: v }))} placeholder="2026-04-01" />
            <Text style={s.fieldLabel}>End Date (YYYY-MM-DD)</Text>
            <TextInput style={s.input} value={form.endDate} onChangeText={v => setForm(p => ({ ...p, endDate: v }))} placeholder="2026-04-05" />
            <Text style={s.fieldLabel}>Reason</Text>
            <TextInput style={[s.input, { height: 80, textAlignVertical: 'top' }]}
              value={form.reason} onChangeText={v => setForm(p => ({ ...p, reason: v }))}
              placeholder="Brief reason for leave request..." multiline />
          </ScrollView>
        </SafeAreaView>
      </Modal>
    </SafeAreaView>
  );
}

const s = StyleSheet.create({
  safe: { flex: 1, backgroundColor: '#f8fafc' },
  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 16, paddingVertical: 14 },
  back: { padding: 4 }, addBtn: { padding: 4 },
  headerTitle: { color: '#fff', fontSize: 18, fontWeight: '700' },
  balanceRow: { flexDirection: 'row', gap: 12, padding: 16 },
  balanceCard: { flex: 1, backgroundColor: '#fff', borderRadius: 16, padding: 16, borderTopWidth: 4, shadowColor: '#000', shadowOpacity: 0.06, shadowRadius: 8, elevation: 3 },
  balanceLabel: { fontSize: 12, fontWeight: '600', color: '#64748b', marginBottom: 4 },
  balanceDays: { fontSize: 36, fontWeight: '900' },
  balanceSub: { fontSize: 11, color: '#94a3b8', marginBottom: 8 },
  progressBar: { height: 6, backgroundColor: '#e2e8f0', borderRadius: 3 },
  progressFill: { height: 6, borderRadius: 3 },
  section: { margin: 16 },
  sectionTitle: { fontSize: 16, fontWeight: '800', color: '#1e293b', marginBottom: 12 },
  emptyContainer: { alignItems: 'center', padding: 32 },
  empty: { color: '#94a3b8', fontSize: 14, marginTop: 8 },
  emptyBtn: { marginTop: 16, backgroundColor: P, paddingHorizontal: 24, paddingVertical: 10, borderRadius: 12 },
  emptyBtnText: { color: '#fff', fontWeight: '700' },
  requestCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#fff', borderRadius: 12, padding: 14, marginBottom: 8, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 4, elevation: 1 },
  typeBadge: { paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8 },
  typeText: { fontSize: 12, fontWeight: '700' },
  requestDates: { fontSize: 13, fontWeight: '600', color: '#1e293b' },
  requestReason: { fontSize: 11, color: '#64748b', marginTop: 2 },
  statusBadge: { paddingHorizontal: 8, paddingVertical: 3, borderRadius: 6 },
  statusText: { fontSize: 10, fontWeight: '800' },
  modalHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 16, borderBottomWidth: 1, borderBottomColor: '#e2e8f0' },
  modalTitle: { fontSize: 16, fontWeight: '700', color: '#1e293b' },
  submitText: { color: P, fontWeight: '700', fontSize: 15 },
  fieldLabel: { fontSize: 12, fontWeight: '700', color: '#374151', marginBottom: 6 },
  input: { backgroundColor: '#fff', borderRadius: 10, borderWidth: 1, borderColor: '#e2e8f0', padding: 12, marginBottom: 16, fontSize: 14, color: '#1e293b' },
  chip: { paddingHorizontal: 14, paddingVertical: 8, borderRadius: 20, borderWidth: 1.5, borderColor: '#e2e8f0', marginRight: 8, backgroundColor: '#fff' },
  chipActive: { backgroundColor: P, borderColor: P },
  chipText: { fontSize: 12, fontWeight: '600', color: '#64748b' },
  chipTextActive: { color: '#fff' },
});
